import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import Icon from '../../components/AppIcon';
import MetricsCard from '../dashboard-overview/components/MetricsCard';
import RequestCard from './components/RequestCard';
import RequestDetailsModal from './components/RequestDetailsModal';
import ApprovalDialog from './components/ApprovalDialog';
import InviteVendorModal from '../vendor-approval-workflow/components/InviteVendorModal';
import SupplierRequestFilters from './components/SupplierRequestFilters';
import TablePagination from '../vendor-master-list/components/TablePagination';
import { useToast } from '../../components/ui/Toast';
import { supplierRequestService, getErrorMessage } from '../../services/apiService';

const SupplierRequests = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  // request_id -> downstream onboarding facts (invite/vendor/docs/approvals)
  const [journeyById, setJourneyById] = useState({});
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedRequestForInvite, setSelectedRequestForInvite] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });

  const [stats, setStats] = useState({
    draft: 0,
    submitted: 0,
    approved: 0,
    rejected: 0
  });

  // Case-insensitive role checks
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';
  const isCoordinator = user?.role?.toUpperCase() === 'COORDINATOR';

  // Fetch requests only on mount (status and search filtering are client-side, pagination is client-side)
  useEffect(() => {
    fetchRequests();
    fetchJourneys();
  }, []);

  // Client-side filtering by status and search (no API call, instant filtering)
  const filteredRequests = useMemo(() => {
    let filtered = [...requests];
    
    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(request => {
        const requestStatus = request.status?.toUpperCase();
        return requestStatus === filters.status.toUpperCase();
      });
    }
    
    // Client-side search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(request =>
        request.required_vendor_name?.toLowerCase().includes(searchLower) ||
        request.vendor_email?.toLowerCase().includes(searchLower) ||
        request.vendor_contact?.toLowerCase().includes(searchLower) ||
        request.user_name?.toLowerCase().includes(searchLower)
      );
    }
    
    // PRIORITY SORTING: SUBMITTED (Pending) first, then by date (newest first)
    filtered.sort((a, b) => {
      // Priority 1: SUBMITTED status first
      const aIsSubmitted = a.status?.toUpperCase() === 'SUBMITTED';
      const bIsSubmitted = b.status?.toUpperCase() === 'SUBMITTED';
      
      if (aIsSubmitted && !bIsSubmitted) return -1;
      if (!aIsSubmitted && bIsSubmitted) return 1;
      
      // Priority 2: Newest first within same status
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    
    return filtered;
  }, [requests, filters.status, filters.search]);

  // Client-side pagination for filtered results
  const paginatedRequests = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    return filteredRequests.slice(startIndex, endIndex);
  }, [filteredRequests, pagination.page, pagination.limit]);

  // Update pagination totals based on filtered results
  useEffect(() => {
    const total = filteredRequests.length;
    const totalPages = Math.ceil(total / pagination.limit);
    setPagination(prev => ({
      ...prev,
      total: total,
      totalPages: totalPages
    }));
  }, [filteredRequests.length, pagination.limit]);

  // Journey strip data. Non-critical chrome: a failure leaves the cards
  // rendering exactly as before, so no error toast.
  const fetchJourneys = async () => {
    try {
      const res = await supplierRequestService.getJourneyStatus();
      const map = {};
      (res?.data || []).forEach((j) => { map[j.request_id] = j; });
      setJourneyById(map);
    } catch (err) {
      console.error('Error fetching journey status:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      
      // Build query params - fetch all requests (status and search are filtered client-side)
      const params = {
        page: 1,
        limit: 1000 // Fetch a large batch for client-side filtering
      };
      
      const response = await supplierRequestService.getSupplierRequests(params);
      
      const data = response?.data || response || [];
      const meta = response?.meta || {};
      
      setRequests(data);
      
      // Update pagination state based on filtered results (will be calculated in useMemo)
      const total = data.length;
      const totalPages = Math.ceil(total / pagination.limit);
      
      setPagination(prev => ({
        ...prev,
        total: total,
        totalPages: totalPages,
        page: 1 // Reset to first page when fetching new data
      }));
      
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats separately (all requests for accurate stats)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await supplierRequestService.getSupplierRequests({
          page: 1,
          limit: 1000 // Get all for stats
        });
        const data = response?.data || response || [];
        calculateStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };
    fetchStats();
  }, []);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1 // Reset to first page when changing limit
    }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({
      ...prev,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleMetricClick = (statusFilter) => {
    // If clicking the same status, clear the filter; otherwise set it
    const newStatus = filters.status === statusFilter ? '' : statusFilter;
    handleFiltersChange({ ...filters, status: newStatus });
    // Reset to first page when status filter changes (client-side pagination will handle display)
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  const calculateStats = (data) => {
    // Handle both lowercase and uppercase status values
    const stats = {
      draft: data.filter(r => r.status?.toLowerCase() === 'draft').length,
      submitted: data.filter(r => r.status?.toLowerCase() === 'submitted').length,
      approved: data.filter(r => r.status?.toLowerCase() === 'approved').length,
      rejected: data.filter(r => r.status?.toLowerCase() === 'rejected').length
    };
    setStats(stats);
  };

  const handleViewDetails = async (request) => {
    try {
      // Fetch full request details to ensure we have invite information
      const response = await supplierRequestService.getRequestById(request.id);
      const fullRequest = response?.data || response || request;
      setSelectedRequest(fullRequest);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching request details:', error);
      // Fallback to the request from list if fetch fails
      setSelectedRequest(request);
      setShowDetailsModal(true);
    }
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setApprovalAction('approve');
    setShowApprovalDialog(true);
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setApprovalAction('reject');
    setShowApprovalDialog(true);
  };

  const handleApprovalComplete = (updatedRequest) => {
    if (updatedRequest) {
      // Update the request in local state
      setRequests(prevRequests => {
        const updated = prevRequests.map(req => 
          req.id === updatedRequest.id ? { ...req, ...updatedRequest } : req
        );
        // Recalculate stats based on updated data
        calculateStats(updated);
        return updated;
      });
    }
    
    // Close modals
    setShowApprovalDialog(false);
    setShowDetailsModal(false);
    setSelectedRequest(null);
    
    // NO fetchRequests() call - preserves scroll and pagination
    fetchJourneys(); // journey strip only; does not touch the list
  };

  const handleInviteVendor = (request) => {
    setSelectedRequestForInvite(request);
    setShowInviteModal(true);
  };

  const handleInviteSuccess = async () => {
    // Update request in local state instead of refetching
    if (selectedRequestForInvite) {
      setRequests(prevRequests => {
        const updated = prevRequests.map(req =>
          req.id === selectedRequestForInvite.id
            ? {
                ...req,
                vendor_invited: true,
                vendor_invited_at: new Date().toISOString(),
                vendor_invite_id: req.vendor_invite_id || selectedRequestForInvite.vendor_invite_id,
                // Drop the previous invite snapshot. On a re-invite it still
                // holds the OLD expires_at, which would leave the card reading
                // "Invite expired" immediately after a successful re-send.
                // Clearing it resolves to plain "Vendor Invited" until the next
                // fetch supplies the real new expiry — better than inventing one.
                invite: null
              }
            : req
        );
        return updated;
      });
    }
    
    setShowInviteModal(false);
    setShowDetailsModal(false);
    toast.success('Vendor invitation sent successfully!');
    // NO fetchRequests() - preserves scroll
    fetchJourneys(); // journey strip only; does not touch the list
  };

  const handleContinueDraft = (request) => {
    // Navigate to supplier request form with draft loaded for editing
    navigate(`/supplier-request-form?draftId=${request.id}`);
  };

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[normalizedStatus] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const icons = {
      draft: 'FileEdit',
      submitted: 'Clock',
      approved: 'CheckCircle',
      rejected: 'XCircle'
    };
    return icons[normalizedStatus] || 'FileText';
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Loading requests...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Transform stats for MetricsCard component
  const metricsData = [
    {
      title: 'Drafts',
      value: stats.draft.toString(),
      change: null,
      changeType: 'neutral',
      icon: 'FileEdit',
      color: 'muted',
      statusFilter: 'DRAFT'
    },
    {
      title: 'Submitted',
      value: stats.submitted.toString(),
      change: null,
      changeType: 'neutral',
      icon: 'Clock',
      color: 'warning',
      statusFilter: 'SUBMITTED'
    },
    {
      title: 'Approved',
      value: stats.approved.toString(),
      change: null,
      changeType: 'neutral',
      icon: 'CheckCircle',
      color: 'success',
      statusFilter: 'APPROVED'
    },
    {
      title: 'Rejected',
      value: stats.rejected.toString(),
      change: null,
      changeType: 'neutral',
      icon: 'XCircle',
      color: 'destructive',
      statusFilter: 'REJECTED'
    }
  ];

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb 
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'Supplier Requests', path: '/supplier-requests' }
        ]} 
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Supplier Requests
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isAdmin ? 'Review and manage all supplier requests' : 'View your supplier requests'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="default"
            onClick={() => navigate('/supplier-request-form')}
            iconName="Plus"
            iconPosition="left"
          >
            New Request
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border border-red-200 shadow-sm bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <span className="text-red-800">{error}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchRequests}
                className="ml-auto"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricsData.map((metric, index) => (
          <MetricsCard
            key={index}
            title={metric.title}
            value={metric.value}
            change={metric.change}
            changeType={metric.changeType}
            icon={metric.icon}
            color={metric.color}
            onClick={() => handleMetricClick(metric.statusFilter)}
            isActive={filters.status === metric.statusFilter}
          />
        ))}
      </div>

      {/* Filters Card */}
      <SupplierRequestFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={() => handleFiltersChange({ status: '', search: '' })}
      />

      {/* Requests List Card */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          {paginatedRequests.length === 0 && !loading ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Inbox" size={40} className="text-slate-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No Requests Found</h3>
              <p className="text-base text-slate-600 mb-6 max-w-md mx-auto">
                {Object.values(filters).some(value => value && value !== '') 
                  ? 'Try adjusting your filters to see more results.' 
                  : 'No supplier requests found. Create a new request to get started.'
                }
              </p>
              {Object.values(filters).some(value => value && value !== '') ? (
                <Button
                  variant="outline"
                  onClick={() => handleFiltersChange({ status: '', search: '' })}
                >
                  Clear Filters
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={() => navigate('/supplier-request-form')}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Create New Request
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {paginatedRequests.map(request => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    journey={journeyById[request.id]}
                    isAdmin={isAdmin}
                    isCoordinator={isCoordinator}
                    onViewDetails={handleViewDetails}
                    onApprove={handleApproveClick}
                    onReject={handleRejectClick}
                    onInviteVendor={handleInviteVendor}
                    onContinueDraft={handleContinueDraft}
                    getStatusColor={getStatusColor}
                    getStatusIcon={getStatusIcon}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total > 0 && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemLabel="requests"
        />
      )}

      {/* Modals */}
      {showDetailsModal && selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          isAdmin={isAdmin}
          isCoordinator={isCoordinator}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          onInviteVendor={handleInviteVendor}
          onContinueDraft={handleContinueDraft}
          getStatusColor={getStatusColor}
        />
      )}

      {showApprovalDialog && selectedRequest && (
        <ApprovalDialog
          request={selectedRequest}
          action={approvalAction}
          onClose={() => {
            setShowApprovalDialog(false);
            setApprovalAction(null);
          }}
          onComplete={handleApprovalComplete}
        />
      )}

      {showInviteModal && selectedRequestForInvite && (
        <InviteVendorModal
          isOpen={true}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedRequestForInvite(null);
          }}
          onSuccess={handleInviteSuccess}
          prefillData={{
            name: selectedRequestForInvite.required_vendor_name,
            email: selectedRequestForInvite.vendor_email
          }}
          requestId={selectedRequestForInvite.id}
        />
      )}
    </div>
  );
};

export default SupplierRequests;
